<script setup lang="ts">
const colorMode = useColorMode()
const { user, signOutUser } = useAuth()
const { isHelpSlideoverOpen } = useDashboard()
const { isDashboardSearchModalOpen } = useUIState()
const { metaSymbol } = useShortcuts()

const colorModeText = computed(() =>
  colorMode.value === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'
)

const avatarSrc = computed(() => {
  if (!user.value?.picture) return ''

  try {
    if (user.value.picture.startsWith('https://lh3.googleusercontent.com')) {
      const baseUrl = user.value.picture.split('?')[0]
      return `${baseUrl}?sz=96`
    }
    return user.value.picture
  } catch {
    return ''
  }
})

const items = computed(() => [
  [
    {
      slot: 'account',
      label: '',
      disabled: true
    }
  ],
  [
    {
      label: colorModeText.value,
      icon: colorMode.value === 'dark' ? 'i-heroicons-sun' : 'i-heroicons-moon',
      click: () => {
        colorMode.preference = colorMode.value === 'dark' ? 'light' : 'dark'
      }
    },
    {
      label: 'Command menu',
      icon: 'i-heroicons-command-line',
      shortcuts: [metaSymbol.value, 'K'],
      click: () => {
        isDashboardSearchModalOpen.value = true
      }
    },
    {
      label: 'Help & Support',
      icon: 'i-heroicons-question-mark-circle',
      shortcuts: ['?'],
      click: () => (isHelpSlideoverOpen.value = true)
    }
  ],
  [
    {
      label: 'Install SRX Launcher',
      icon: 'i-heroicons-arrow-down-tray',
      to: '/install',
      target: '_blank'
    },
    {
      label: 'Documentation',
      icon: 'i-heroicons-book-open',
      to: '/docs',
      target: '_blank'
    },
    {
      label: 'GitHub repository',
      icon: 'i-simple-icons-github',
      to: 'https://github.com/SonderResearchX/srx-dp-frontend',
      target: '_blank'
    }
  ],
  [
    {
      label: 'Sign out',
      icon: 'i-heroicons-arrow-left-on-rectangle',
      click: async () => {
        try {
          await signOutUser()
        } catch (error) {
          console.error('[UserDropdown] Sign out failed:', error)
          const toast = useToast()
          toast.add({
            title: 'Error',
            description: 'Failed to sign out. Please try again.',
            color: 'red'
          })
        }
      }
    }
  ]
])
</script>

<template>
  <UDropdown
    mode="hover"
    :items="items"
    :ui="{ width: 'w-full', item: { disabled: 'cursor-text select-text' } }"
    :popper="{ strategy: 'absolute', placement: 'top' }"
    class="w-full !mt-0"
  >
    <template #default="{ open }">
      <UButton
        color="gray"
        variant="ghost"
        class="w-full"
        :label="user ? `${user.givenName} ${user.familyName}` : 'Anonymous'"
        :class="[open && 'bg-gray-50 dark:bg-gray-800']"
      >
        <template #leading>
          <UAvatar
            :src="avatarSrc"
            size="xs"
            class="object-cover"
            :ui="{
              base: 'ring-1 ring-gray-200 dark:ring-gray-800',
              background: 'bg-gray-100 dark:bg-gray-800'
            }"
            :alt="user?.name ?? 'Anonymous'"
            loading="eager"
          />
        </template>

        <!-- :icon="avatarSrc ? undefined : 'i-heroicons-user-circle'" -->
        <template #trailing>
          <UIcon name="i-heroicons-ellipsis-vertical" class="w-5 h-5 ml-auto" />
        </template>
      </UButton>
    </template>

    <template #account>
      <div class="text-left">
        <p>Signed in as</p>
        <p class="truncate font-medium text-gray-900 dark:text-white">
          {{ user?.email }}
        </p>
      </div>
    </template>

    <template #color-mode>
      <UColorModeButton
        class="w-full text-sm"
        variant="ghost"
        :ui="{
          base: 'flex items-center w-full gap-2 cursor-pointer select-none',
          padding: 'px-2.5 py-2',
          icon: { base: 'w-4 h-4' }
        }"
      >
        <span>{{ colorModeText }}</span>
      </UColorModeButton>
    </template>
  </UDropdown>
</template>
